import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";

const CrmContext = createContext(null);
export const useCrm = () => useContext(CrmContext);
const upsert = (setter) => (row) => setter((cur) => (cur.some((x) => x.id === row.id) ? cur.map((x) => (x.id === row.id ? row : x)) : [row, ...cur]));

export const CrmProvider = ({ children }) => {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const [scope, setScope] = useState("all");
  const [leads, setLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [team, setTeam] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sms, setSms] = useState({ configured: false });
  const [notifications, setNotifications] = useState([]);
  const [notificationsError, setNotificationsError] = useState(false);
  const [pondLeads, setPondLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const effective = isOwner ? scope : "mine";
  const workScope = effective === "unassigned" ? "all" : effective;

  const reload = useCallback(async () => {
    try {
      const [l, t, a, ponds] = await Promise.all([
        api.get("/admin/leads", { params: { scope: effective } }), api.get("/admin/tasks", { params: { scope: workScope } }),
        api.get("/admin/appointments", { params: { scope: workScope } }), api.get("/admin/crm/pond-leads")]);
      setLeads(l.data || []); setTasks(t.data || []); setAppointments(a.data || []); setPondLeads(ponds.data || []);
    } catch (e) { toast.error(errorMessage(e)); } finally { setLoading(false); }
  }, [effective, workScope]);
  const loadTemplates = useCallback(() => api.get("/admin/crm/templates").then(({ data }) => setTemplates(data || [])), []);
  const loadPlans = useCallback(() => api.get("/admin/crm/action-plans").then(({ data }) => setPlans(data || [])), []);
  const loadSms = useCallback(() => api.get("/admin/crm/sms/settings").then(({ data }) => setSms(data)), []);
  const loadNotifications = useCallback(() => api.get("/admin/crm/notifications", { params: { scope: effective } }).then(({ data }) => { setNotifications(data || []); setNotificationsError(false); }).catch(() => setNotificationsError(true)), [effective]);

  useEffect(() => { reload(); const id = setInterval(reload, 60000); return () => clearInterval(id); }, [reload]);
  useEffect(() => {
    loadTemplates().catch(() => {}); loadPlans().catch(() => {}); loadSms().catch(() => {});
    if (isOwner) api.get("/auth/admin/team-users").then(({ data }) => setTeam(data || [])).catch(() => {});
  }, [isOwner, loadTemplates, loadPlans, loadSms]);
  useEffect(() => { loadNotifications(); const id = setInterval(loadNotifications, 30000); return () => clearInterval(id); }, [loadNotifications]);

  const upsertLead = useMemo(() => upsert(setLeads), []);
  const upsertTask = useMemo(() => upsert(setTasks), []);
  const upsertAppointment = useMemo(() => upsert(setAppointments), []);
  const patchLead = useCallback(async (id, values) => {
    try { const { data } = await api.patch(`/admin/leads/${id}`, values); upsertLead(data); return data; } catch (e) { toast.error(errorMessage(e)); return null; }
  }, [upsertLead]);
  const removeLeads = useCallback((ids) => { setLeads((cur) => cur.filter((l) => !ids.includes(l.id))); setTasks((cur) => cur.filter((t) => !ids.includes(t.lead_id))); }, []);
  const openPerson = useCallback((id, mode) => { const next = new URLSearchParams(params); next.delete("mode"); if (id) next.set("person", id); else next.delete("person"); if (id && mode) next.set("mode", mode); setParams(next); }, [params, setParams]);
  const go = useCallback((view, extra = {}) => { const next = new URLSearchParams({ view, ...extra }); setParams(next); }, [setParams]);

  const value = {
    user, isOwner, scope: effective, setScope, leads, pondLeads, tasks, appointments, team, templates, plans, sms, notifications, notificationsError, loading, params,
    reload, loadTemplates, loadPlans, loadSms, loadNotifications, upsertLead, upsertTask, upsertAppointment, patchLead, removeLeads, openPerson, go,
    setTasks, setAppointments,
  };
  return <CrmContext.Provider value={value}>{children}</CrmContext.Provider>;
};
